import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import { Button, classNames, List, Select, TextInput, useToggle } from '@pella/sash-framework';
import { isValidArray } from '../../../../utilities/validations';

const FeedbackQuestion = ({ data, className, formType, onAnswerClick, currentValues, currentFlow, onClose }) => {
	const [otherAnswer, setOtherAnswer] = useState('');
	const [otherError, setOtherError] = useState(null);
	const { open, onToggleClick } = useToggle();

	const headerClasses = classNames({
		'feedback-survey-header mb-3 pr-8': true,
		'border-b border-b-neutral-200 pt-6 pb-3 mx-6 text-h3': formType === 'a',
		'pt-5 px-5 text-subtitle font-bold': formType === 'b',
	});

	const contentClasses = classNames({
		'feedback-survey-content flex-1 pb-6 flex flex-col h-full': true,
		'px-6': formType === 'a',
		'px-5 mt-4 md:mt-0': formType === 'b',
	});

	const subheadingClasses = classNames({
		'feedback-survey-subheading text-neutral-600 flex-none': true,
		'max-md:text-body-2': formType === 'a',
		'text-body-2 text-center md:text-left': formType === 'b',
	});

	const questionClasses = classNames({
		'feedback-question-text mb-4 pt-6 md:pt-0 text-center text-pretty': true,
		'text-subtitle font-bold': formType === 'a',
		'text-subtitle font-bold md:text-subtitle-2 md:text-left': formType === 'b',
	});

	const stripHtmlTags = (text) => text.replace(/<[^>]*>/g, '');

	const onSelectChange = (event) => {
		const selectedOption = event.target.selectedOptions[0];
		const flow = selectedOption.getAttribute('data-flow') ? selectedOption.getAttribute('data-flow')?.split(',') : '';
		const answer_url = selectedOption.getAttribute('data-url') || null;
		const answer = selectedOption.value;

		if (answer === '') return;

		if (answer === 'Other') {
			onToggleClick();
		} else {
			onAnswerClick({ answer, flow, answer_url });
		}
	};

	const onChatClick = async () => {
		const chatExists = document.querySelector('.quiq-action-toggle');

		if (chatExists) {
			await onClose();
			chatExists.click();
		} else {
			await onClose();
			navigate('/support/');
		}
	};

	const onOtherSubmit = (answer) => {
		if (!otherAnswer) {
			setOtherError('Please describe your answer.');
		} else {
			setOtherError(null);
			onAnswerClick({ answer: answer.answer, flow: answer.flow, answer_url: answer.answer_url });
			onToggleClick();
		}
	};

	const onYesAnswer = async (answer_url) => {
		await onClose();
		navigate(answer_url);
	};

	return (
		<div
			className={`feedback-question flex h-full flex-col overflow-auto ${className || ''}`}
			data-testid='feedback-question'
		>
			<p className={headerClasses}>{data.heading}</p>

			<div
				className={contentClasses}
				data-testid={`question-${data.number}`}
			>
				{data.subheading && <p className={subheadingClasses}>{data.subheading}</p>}

				<div className='flex flex-1 flex-col justify-center'>
					{data.question && (
						<div
							dangerouslySetInnerHTML={{ __html: data.question }}
							className={questionClasses}
						/>
					)}

					{/* Form A UI & Form B Desktop UI */}
					{isValidArray(data.answers) && (
						<List
							bullets='none'
							className={`feedback-survey-answers flex-wrap gap-2.5 md:gap-4 ${formType === 'b' ? 'hidden md:flex' : 'flex'}`}
						>
							{data.answers.map((answer, index) => (
								<li
									key={index}
									className={`w-full ${data.answers.length > 2 ? '' : 'md:flex-1'}`}
								>
									<Button
										color='secondary'
										className='!h-12 !py-0 hover:bg-black hover:text-white hover:!opacity-100'
										onClick={answer.answer === 'Other' ? onToggleClick : () => onAnswerClick(answer)}
										wide
									>
										<span
											dangerouslySetInnerHTML={{ __html: answer.answer }}
											className='[&>span]:text-xs [&>span]:font-normal [&>span]:normal-case [&>span]:tracking-[0.4px]'
										/>
									</Button>

									{answer.answer === 'Other' && open && (
										<div className='mt-4 flex flex-row items-start gap-x-2.5'>
											<TextInput
												id={`question${data.number}-other`}
												labelText='Other'
												onChange={(event) => setOtherAnswer(event.target.value)}
												className='h-10 !min-h-0 !p-2'
												groupClass='w-full !mb-0'
												placeholder='Please describe your answer.'
												error={otherError ? true : false}
												errorText={otherError}
												hideLabel
											/>

											<Button
												color='tertiary-alt'
												onClick={() => onOtherSubmit(answer)}
												size='sm'
												className='mt-1 !w-fit !px-0 !py-1'
											>
												Submit
											</Button>
										</div>
									)}
								</li>
							))}
						</List>
					)}

					{/* Form B Mobile UI */}
					{formType === 'b' && isValidArray(data.answers) && (
						<div className='md:hidden'>
							<Select
								id='feedback-survey-answers-mobile'
								labelText='Select Option'
								className='h-12 !min-h-0'
								groupClass='!mb-0'
								onChange={onSelectChange}
								hideLabel
							>
								<option value=''>Select Option</option>
								{data.answers.map((answer, index) => (
									<option
										key={index}
										data-flow={answer.flow}
										data-url={answer.answer_url}
										value={answer.answer}
									>
										{stripHtmlTags(answer.answer)}
									</option>
								))}
							</Select>

							{open && (
								<div className='mt-4 flex flex-row items-center gap-x-2.5'>
									<TextInput
										id={`question${data.number}-other`}
										labelText='Other'
										onChange={(event) => setOtherAnswer(event.target.value)}
										className='h-10 !min-h-0 !p-2'
										groupClass='w-full !mb-0'
										placeholder='Please describe your answer.'
										hideLabel
									/>

									<Button
										color='tertiary-alt'
										onClick={() => onOtherSubmit(answer)}
										size='sm'
										className='!px-0 !py-1'
									>
										Submit
									</Button>
								</div>
							)}
						</div>
					)}

					{/* Non-Other Thank You Message */}
					{data.number === 5 && !currentFlow?.includes('other') && (
						<Button
							color='secondary'
							onClick={currentValues.question4.answer === 'Yes' ? () => onClose() : () => onYesAnswer(currentValues.question3.answer_url)}
							className='!h-12 !py-0 hover:bg-black hover:text-white hover:!opacity-100'
						>
							{currentValues.question4.answer === 'No' ? currentValues.question3.answer?.replace('To ', '') : 'Back to Pella.com'}
						</Button>
					)}

					{/* Other Thank You Message */}
					{data.number === 5 && currentFlow?.includes('other') && (
						<Button
							color='secondary'
							className='!h-12 !py-0 hover:bg-black hover:text-white hover:!opacity-100'
							onClick={onChatClick}
						>
							Chat with a Rep
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

FeedbackQuestion.propTypes = {
	data: PropTypes.object.isRequired,
	onAnswerClick: PropTypes.func.isRequired,
	className: PropTypes.string,
	formType: PropTypes.oneOf(['a', 'b']),
	currentValues: PropTypes.object,
	currentFlow: PropTypes.array,
	onClose: PropTypes.func,
};

export default FeedbackQuestion;
